import { useState, useEffect } from 'react';
import { Copy, Check, Link2 } from 'lucide-react';
import { toast } from 'sonner';

import { api } from '../services/client';
import { useTranslation } from '../i18n';
import { getImageUrl } from '../utils/imageUrl';
import { ISpace } from '../types';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Switch } from './ui/switch';
import ImagePreviewOverlay from './ImagePreviewOverlay';

interface IInvitationLinkData {
  code: string;
  link: string;
}

interface ISpaceSettingsSectionProps {
  space: ISpace;
  newRequireVerify: boolean;
  setNewRequireVerify: (v: boolean) => void;
  newVerifyUrl: string;
  setNewVerifyUrl: (v: string) => void;
  handleSaveVerify: () => void;
  verifySaveMsg: { type: 'success' | 'error'; message: string } | null;
  onSpaceUpdated?: (space: ISpace) => void;
  onToggleVisibility?: () => void;
}

const SpaceSettingsSection = ({
  space,
  newRequireVerify,
  setNewRequireVerify,
  newVerifyUrl,
  setNewVerifyUrl,
  handleSaveVerify,
  verifySaveMsg,
  onSpaceUpdated,
  onToggleVisibility,
}: ISpaceSettingsSectionProps) => {
  const { t } = useTranslation();
  const [linkData, setLinkData] = useState<IInvitationLinkData | null>(null);
  const [linkLoading, setLinkLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [suffix, setSuffix] = useState('');

  useEffect(() => {
    loadInvitationLink();
  }, [space.id]);

  const loadInvitationLink = async () => {
    setLinkLoading(true);

    try {
      const data = await api.get<IInvitationLinkData | null>(
        `/backoffice/spaces/${space.id}/invitation-link`,
      );
      setLinkData(data);
    } catch (err) {
      console.error('Failed to load invitation link:', err);
    } finally {
      setLinkLoading(false);
    }
  };

  const handleGenerate = async (keepPrefix = false) => {
    if (keepPrefix && (!suffix || !/^\d{2,3}$/.test(suffix))) {
      toast.error(t('space_settings.suffix_invalid'));
      return;
    }
    setGenerating(true);

    try {
      const body: { suffix?: string; prefix?: string } = {};
      if (suffix && /^\d{2,3}$/.test(suffix)) {
        body.suffix = suffix;
      }
      if (keepPrefix && linkData?.code) {
        body.prefix = linkData.code.slice(0, 3);
      }
      const data = await api.post<IInvitationLinkData>(
        `/backoffice/spaces/${space.id}/invitation-link/generate`,
        body,
      );
      setLinkData(data);
      toast.success(t('space_settings.link_generated'));
    } catch (err: any) {
      toast.error(err.message || t('space_settings.generate_failed'));
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    if (!linkData) return;

    navigator.clipboard.writeText(linkData.link);
    setCopied(true);
    toast.success(t('common.copied'));
    setTimeout(() => setCopied(false), 2000);
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const r = await api.upload<{ imageUrl: string }>(
        `/backoffice/spaces/${space.id}/upload-avatar`,
        file,
      );
      toast.success(t('space_settings.avatar_upload_success'));
      onSpaceUpdated?.({ ...space, avatar: r.imageUrl });
    } catch (err: any) {
      toast.error(err.message || t('space_settings.upload_avatar_failed'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const isPrivate =
    space.visibility === 'private' || space.joinRule === 'invite_only';

  return (
    <>
      <Card className="rounded-[10px] p-6">
        <CardContent className="p-0">
          {/* Section Title */}
          <div className="flex items-center gap-2 mb-6">
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
            >
              <defs>
                <linearGradient
                  id="settings-gradient"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#000000" />
                  <stop offset="100%" stopColor="#000000" />
                </linearGradient>
              </defs>
              <path
                stroke="url(#settings-gradient)"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 010-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.28z"
              />
              <path
                stroke="url(#settings-gradient)"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <h2 className="text-lg font-semibold text-[#101828]">
              {t('space_settings.title')}
            </h2>
          </div>

          {/* Avatar */}
          <div className="flex items-start gap-4 pb-6 border-b">
            <button
              type="button"
              disabled={!space.avatar}
              aria-label={t('common.view_avatar')}
              className={`w-20 h-20 rounded-lg bg-linear-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-2xl font-bold flex-shrink-0 overflow-hidden${space.avatar ? ' cursor-pointer' : ''}`}
              onClick={() => {
                if (space.avatar) setPreviewImage(getImageUrl(space.avatar)!);
              }}
            >
              {space.avatar ? (
                <img
                  src={getImageUrl(space.avatar)}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : (
                space.name?.charAt(0)?.toUpperCase() || '🏠'
              )}
            </button>
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-700">
                {t('space_settings.avatar')}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {t('space_settings.avatar_hint')}
              </div>
              <label className="mt-1.5 text-xs text-[#4A5565] hover:text-[#101828] flex items-center gap-1 cursor-pointer border border-gray-300 rounded-md px-2.5 py-1 w-fit hover:border-gray-400 transition-colors">
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
                  />
                </svg>
                {uploading
                  ? t('common.uploading')
                  : t('space_settings.upload_avatar')}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                  disabled={uploading}
                  onChange={handleAvatarUpload}
                />
              </label>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-border" />

          {/* Description */}
          <div className="py-5">
            <div className="text-sm font-medium text-[#101828]">
              {t('space_settings.description')}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {space.description || t('space_settings.no_description')}
            </p>
          </div>

          {/* Divider */}
          <div className="border-t border-border" />

          {/* Space Type */}
          <div className="py-5">
            <div className="text-sm font-medium text-muted-foreground mb-3">
              {t('space_settings.space_type')}
            </div>
            <div className="flex items-center justify-between p-4 border border-border rounded-lg">
              <div className="flex items-center gap-3">
                {isPrivate ? (
                  <svg
                    className="w-5 h-5 text-purple-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5 text-blue-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
                    <path d="M2 12h20" />
                  </svg>
                )}
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {isPrivate
                      ? t('space_settings.private_space')
                      : t('space_settings.public_space')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {isPrivate
                      ? t('space_settings.private_desc')
                      : t('space_settings.public_desc')}
                  </p>
                </div>
              </div>
              <Switch
                checked={!isPrivate}
                onCheckedChange={() => onToggleVisibility?.()}
              />
            </div>
          </div>

          <div className="border-t border-border" />

          {/* External Verification */}
          <div className="py-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-[#101828]">
                  {t('space_settings.external_verify')}
                </div>
                <div className="text-xs text-[#4A5565] mt-0.5">
                  {newRequireVerify
                    ? t('space_settings.verify_enabled')
                    : t('space_settings.verify_disabled')}
                </div>
              </div>
              <Switch
                checked={newRequireVerify}
                onCheckedChange={setNewRequireVerify}
              />
            </div>
            {newRequireVerify && (
              <Input
                type="text"
                value={newVerifyUrl}
                onChange={(e) => setNewVerifyUrl(e.target.value)}
                placeholder="https://example.com/api/verify"
                className={`mt-3 ${newRequireVerify && !newVerifyUrl?.trim() ? 'border-red-400' : ''}`}
              />
            )}
            {newRequireVerify && !newVerifyUrl?.trim() && (
              <div className="text-xs text-red-500 mt-1">
                {t('space_settings.verify_url_required')}
              </div>
            )}
            <div className="mt-3 flex items-center gap-3">
              <Button onClick={handleSaveVerify} className="px-5 py-2.5">
                {t('space_settings.save_verify')}
              </Button>
              {verifySaveMsg && (
                <span
                  className={`text-sm ${verifySaveMsg.type === 'success' ? 'text-green-600' : 'text-red-500'}`}
                >
                  {verifySaveMsg.message}
                </span>
              )}
            </div>
          </div>

          <div className="border-t border-border" />

          {/* Invitation Link */}
          <div className="pt-5">
            <div className="text-sm font-medium text-[#101828]">
              {t('space_settings.invitation_link')}
            </div>
            <p className="text-xs text-[#4A5565] mt-0.5 mb-3">
              {t('space_settings.invitation_desc')}
            </p>

            {linkLoading ? (
              <div className="text-sm text-gray-400">{t('common.loading')}</div>
            ) : (
              <div className="space-y-3">
                {linkData && !linkData.link.startsWith('http') && (
                  <div className="flex items-center gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                    <svg
                      className="w-4 h-4 text-amber-500 shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                      />
                    </svg>
                    <span className="text-xs text-amber-700">
                      {t('space_settings.base_url_warning')}
                    </span>
                  </div>
                )}

                {/* Current code display + copy */}
                {linkData && (
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <Input
                        type="text"
                        readOnly
                        value={
                          linkData.link.startsWith('http')
                            ? linkData.link
                            : linkData.code
                        }
                        className="text-foreground font-mono"
                      />
                    </div>
                    <Button
                      variant="outline"
                      onClick={handleCopy}
                      className="flex items-center gap-1.5 whitespace-nowrap"
                    >
                      {copied ? (
                        <>
                          <Check className="w-4 h-4 text-green-500" />
                          {t('common.copied')}
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          {t('common.copy')}
                        </>
                      )}
                    </Button>
                  </div>
                )}

                {/* Code generation: prefix (readonly) + suffix (editable) + button */}
                <div className="flex items-center gap-2">
                  <div className="flex items-center border rounded-md overflow-hidden">
                    <span className="px-3 py-2 bg-muted text-muted-foreground font-mono text-sm select-none">
                      {linkData?.code?.slice(0, 3) || '???'}
                    </span>
                    <Input
                      type="text"
                      value={suffix}
                      onChange={(e) =>
                        setSuffix(e.target.value.replace(/\D/g, '').slice(0, 3))
                      }
                      placeholder="00"
                      className="w-16 border-0 border-l rounded-none font-mono text-sm"
                      maxLength={3}
                    />
                  </div>
                  {linkData && (
                    <Button
                      variant="outline"
                      onClick={() => handleGenerate(true)}
                      disabled={generating || !/^\d{2,3}$/.test(suffix)}
                      className="flex items-center gap-1.5 whitespace-nowrap"
                    >
                      {t('common.save')}
                    </Button>
                  )}
                  <Button
                    onClick={() => handleGenerate(false)}
                    disabled={generating}
                    className="flex items-center gap-1.5 whitespace-nowrap"
                  >
                    <Link2 className="w-4 h-4" />
                    {generating
                      ? t('common.generating')
                      : linkData
                        ? t('space_settings.regenerate')
                        : t('common.generate_new_link')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Image Preview Overlay */}
      {previewImage && (
        <ImagePreviewOverlay
          src={previewImage}
          alt={space.name}
          onClose={() => setPreviewImage(null)}
        />
      )}
    </>
  );
};

export default SpaceSettingsSection;
